import React, { useEffect, useState } from "react";
import { CircularProgress, styled, Theme } from "@mui/material";
import {
  PatientSummaryData,
  SavedCaseStatus,
  SummaryInput,
  SummarySectionType,
} from "fe-common/graphql";
import { ClinicColors } from "../styles/clinicTheme";
import { ClinicSmallButton } from "../clinic/clinicActionButton";
import { useDaoContext } from "../diseaseview/DaoContext";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";
import { ReactComponent as EditIcon } from "../assets/editIcon2.svg";
import { TextHighlight } from "fe-common/commonTypes/errors";
import { checkSummaryForPII, summaryToText } from "./patientSummary";
import { PatientSummaryError } from "./patientSummaryError";
import { FormInput } from "../layout/formInput";
import { useClinicState } from "ask/src/clinic/clinicStateContext";

//region [[ Styles ]]

const PatientSummaryEditView = styled("div")(
  (props: { theme?: Theme; sectioned?: string }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    gap: 8,
    borderRadius: 4,
    border: `1px solid ${ClinicColors.primary}`,
    padding: props.sectioned === "true" ? 16 : 0,
    "& .clinic-input": {
      width: "100%",
    },
    "& .sectionTitle, .sectionSubTitle": {
      color: `${ClinicColors.primary}99`,
      fontSize: 15,
      fontWeight: 600,
    },
    "& .sectionSubTitle": {
      fontSize: 14,
    },
    "& .MuiOutlinedInput-input, .highlighter": {
      fontSize: 14,
      lineHeight: 1.3,
      width: "100%",
      fontFamily: props.theme.typography.fontFamily,
      color: ClinicColors.textMain,
      borderRadius: 4,
    },
    "& .MuiOutlinedInput-notchedOutline": props.sectioned !== "true" && {
      border: "none",
    },
    "& textarea:focus": {
      outline: "transparent",
    },
  }),
);

const PatientSummaryEditButtons = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: 8,
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientSummaryEditProps {
  caseId: string;
  clinicId: string;
  summary: PatientSummaryData;
  onUpdate: (updatedSections: PatientSummaryData, etag: string) => void;
  onCancel: () => void;
  onError: () => void;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const PatientSummaryEdit = ({
  caseId,
  clinicId,
  summary,
  onCancel,
  onUpdate,
  onError,
}: PatientSummaryEditProps) => {
  //const [chiefComplaint, setChiefComplaint] = useState(summary.main);
  // const [summarySections, setSummarySections] = useState(
  //     summary.sections
  //         .filter((s) => s.type !== SummarySectionType.Assessment)
  //         .map((s) => {
  //           return { ...s, content: s.content.trim() };
  //         }),
  // );
  const [summaryText, setSummaryText] = useState("");
  const [saving, setSaving] = useState(false);
  const [textHighlights, setTextHighlights] = useState<{
    [key: string]: TextHighlight[];
  }>();
  const [errorMsgs, setErrorMsgs] = useState<React.ReactNode[]>(null);
  //const [sectionedSummary, setSectionedSummary] = useState<boolean>(true);
  const dao = useDaoContext();
  const [{ clinicConfig }] = useClinicState();
  const { translate } = useTranslateByGender();

  useEffect(() => {
    setSummaryText(summaryToText(summary));
  }, [summary]);

  // useEffect(() => {
  //   const sectioned = summarySections.some((s) => s.title);
  //   setSectionedSummary(sectioned);
  // }, []);

  function removeTypeName(obj: SummaryInput) {
    const updated = { ...obj };
    delete updated["__typename"];
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (typeof obj[key] === "object" && obj[key] !== null) {
          if (Array.isArray(obj[key])) {
            obj[key].forEach((item) => {
              removeTypeName(item);
            });
          } else {
            removeTypeName(obj[key]);
          }
        } else if (key === "__typename") {
          delete obj[key];
        }
      }
    }

    return updated;
  }

  const handleUpdate = async () => {
    setSaving(true);
    const updatedSummary: PatientSummaryData = { ...summary };
    updatedSummary.sections = [
      {
        type: SummarySectionType.FreeForm,
        content: summaryText.trim(),
      },
    ];
    //check for PII
    if (clinicConfig.standalone.checkPIIWarning) {
      const PIIValidation = await checkSummaryForPII(summaryText, dao);
      if (!PIIValidation.valid) {
        setErrorMsgs(PIIValidation.messages);
        const highlights = {};
        let offset = 0;
        //check chief complaint for PII
        if (updatedSummary.main) {
          highlights["main"] = PIIValidation.highlights.filter(
            (thl) => thl.EndOffset <= updatedSummary.main?.length,
          );
          offset = updatedSummary.main.length + 1;
        }
        //check sections for PII
        updatedSummary.sections.forEach((section) => {
          if (section.content) {
            highlights[section.type] = PIIValidation.highlights
              .filter(
                (thl) =>
                  thl.EndOffset <= section.content.length + offset &&
                  thl.BeginOffset >= offset,
              )
              .map((thl) => ({
                ...thl,
                BeginOffset: thl.BeginOffset - offset,
                EndOffset: thl.EndOffset - offset,
              }));
            offset += section.content.length + 1;
          }
        });
        setTextHighlights(highlights);
        setSaving(false);
        return;
      }
    }

    dao
      .updatePatientSummary(caseId, clinicId, removeTypeName(updatedSummary))
      .then((result) => {
        if (result.status === SavedCaseStatus.Success) {
          onUpdate(updatedSummary, result.etag);
        } else {
          console.log("summary not updated:", result);
          onError();
        }
      })
      .catch((e) => {
        console.error(e);
        onError();
      })
      .finally(() => {
        setSaving(false);
      });
  };
  return (
    <>
      <PatientSummaryEditView className={"patient-summary-edit"}>
        <FormInput
          value={summaryText}
          disabled={saving}
          multiline
          onChange={(e) => {
            setSummaryText(e.target.value);
          }}
          highlights={textHighlights?.[SummarySectionType.FreeForm]}
          id={"FreeFormInput"}
        />

        {/*{summary.main && (*/}
        {/*  <>*/}
        {/*    <div className={"sectionTitle"}>{translate("CHIEF_COMPLAINT")}</div>*/}
        {/*    <FormInput*/}
        {/*      value={chiefComplaint}*/}
        {/*      className={"assessmentInput"}*/}
        {/*      disabled={saving}*/}
        {/*      onChange={(e) => {*/}
        {/*        setChiefComplaint(e.target.value);*/}
        {/*      }}*/}
        {/*      highlights={textHighlights?.["main"]}*/}
        {/*      id={"chiefComplaintInput"}*/}
        {/*    />*/}
        {/*  </>*/}
        {/*)}*/}
        {/*{summarySections.map((section, i) => (*/}
        {/*  <React.Fragment key={"editSection" + i}>*/}
        {/*    {section.title && (*/}
        {/*      <div className={"sectionSubTitle"}>{section.title}</div>*/}
        {/*    )}*/}
        {/*    <FormInput*/}
        {/*      value={section.content}*/}
        {/*      className={"assessmentInput"}*/}
        {/*      disabled={saving}*/}
        {/*      multiline*/}
        {/*      onChange={(e) => {*/}
        {/*        const newVal = e.target.value;*/}
        {/*        setSummarySections((prevState) => {*/}
        {/*          const updatedSections = [...prevState];*/}
        {/*          const updateSection = updatedSections.find(*/}
        {/*            (s) => s.type === section.type,*/}
        {/*          );*/}
        {/*          updateSection.content = newVal;*/}
        {/*          return updatedSections;*/}
        {/*        });*/}
        {/*      }}*/}
        {/*      highlights={textHighlights?.[section.type]}*/}
        {/*      id={section.type + "Input"}*/}
        {/*    />*/}
        {/*  </React.Fragment>*/}
        {/*))}*/}
      </PatientSummaryEditView>
      {errorMsgs && (
        <PatientSummaryError
          style={{ marginTop: 8 }}
          errorMessages={errorMsgs}
        />
      )}
      <PatientSummaryEditButtons>
        <ClinicSmallButton
          onClick={onCancel}
          label={translate("CANCEL")}
          lightMode
          disabled={saving}
        />
        <ClinicSmallButton
          onClick={handleUpdate}
          label={translate("UPDATE_ASSESSMENT")}
          icon={saving ? <CircularProgress size={14} /> : <EditIcon />}
          disabled={saving}
        />
      </PatientSummaryEditButtons>
    </>
  );
};
